import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent } from './ui/ResponsiveLayout';
import BrandSymbol from './ui/BrandSymbol';

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token: string) => {
    try {
      console.log('Verifying email with token:', token.substring(0, 8) + '...');
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.endofanera.ai'}/api/verify-email?token=${token}`, {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus('success');
        setMessage(data.message);
        setEmail(data.email);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setStatus('error');
        setMessage(data.message || 'Email verification failed');
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setStatus('error');
      setMessage('Failed to verify email. Please try again.');
    }
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  const handleResendVerification = () => {
    // TODO: Implement resend verification email functionality
    console.log('Resend verification not implemented yet');
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <BrandSymbol size="4xl" showText={true} variant="dark" />
          </div>
          
          <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-8">
            <div className="flex justify-center mb-6">
              <h1 className="text-2xl font-semibold text-gray-900">Email Verification</h1>
            </div>
            
            {status === 'verifying' && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Verifying your email address...</p>
              </div>
            )}
            
            {status === 'success' && (
              <div className="text-center">
                <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
                  <div className="flex justify-center mb-2">
                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-green-800 mb-2">Email Verified Successfully!</h3>
                  <p className="text-sm text-green-700">{message}</p>
                  {email && (
                    <p className="text-sm text-green-600 mt-2">Verified: {email}</p>
                  )}
                </div>
                
                <p className="text-gray-600 mb-4">You will be redirected to login in a few seconds...</p>
                
                <button
                  onClick={handleLoginRedirect}
                  className="w-full bg-cyan-600 text-white rounded-md py-3 text-base font-medium hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                >
                  Continue to Login
                </button>
              </div>
            )}
            
            {status === 'error' && (
              <div className="text-center">
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                  <div className="flex justify-center mb-2">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-red-800 mb-2">Verification Failed</h3>
                  <p className="text-sm text-red-700">{message}</p>
                </div>
                
                <div className="space-y-3">
                  <button
                    onClick={handleLoginRedirect}
                    className="w-full bg-cyan-600 text-white rounded-md py-3 text-base font-medium hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                  >
                    Go to Login
                  </button>
                  
                  <button
                    onClick={handleResendVerification}
                    className="w-full bg-gray-100 text-gray-700 rounded-md py-3 text-base font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Resend Verification Email
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EmailVerification;